import { useQuery } from '@tanstack/react-query';
import useUser from 'utils/useUser';
import dataService from 'services/dataService';
import { API_ADMIN_PANEL_AGENCY_DOCTORS } from 'services/dataService/resources';
import QueryParams from 'utils/queryParams';

import type { IAdminPanelAgencyDoctorsResponse } from 'infrastructure/interfaces';

export interface IMemberForPod {
  loginGuid: string;
  name: string;
  role: string;
}

const PROFESSION_ABBREVIATIONS: Record<string, string> = {
  registeredNurse: 'RN',
  medicalAssistant: 'MA',
  doctor: 'DR',
  nursePractitioner: 'NP',
  physicianAssistant: 'PA',
  QHCP: 'QHCP',
};

const abbreviateRole = (role: string): string =>
  PROFESSION_ABBREVIATIONS[role.trim()] ?? role.trim();

const PRIORITY_ROLES = ['RN', 'MA', 'NP', 'PA', 'QHCP'];

const extractPrimaryRole = (roles: string): string => {
  if (!roles) return '';
  // roles is comma-separated (e.g. "registeredNurse, medicalAssistant")
  const roleList = roles
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean);
  const abbreviated = roleList.map(abbreviateRole);

  // 1. Prioritize RN, MA, NP, PA, QHCP (in that order)
  for (const priority of PRIORITY_ROLES) {
    const idx = abbreviated.indexOf(priority);
    if (idx !== -1) return abbreviated[idx];
  }

  // 2. Next, pick the first role that has a known abbreviation
  const knownValues = new Set(Object.values(PROFESSION_ABBREVIATIONS));
  for (const abbr of abbreviated) {
    if (knownValues.has(abbr)) return abbr;
  }

  // 3. Fallback to the first role in the list
  return abbreviated[0] ?? '';
};

const MEMBERS_FOR_POD_KEY = 'membersForPod';
const FIVE_MINUTES = 5 * 60 * 1000;

export const useMembersForPod = () => {
  const user = useUser();
  const agencyGuid = user.agencies?.[0]?.guid;

  const {
    data: members = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [MEMBERS_FOR_POD_KEY, agencyGuid],
    enabled: !!agencyGuid || user.isAdmin,
    queryFn: async () => {
      const filtersString = QueryParams.stringify({
        ...(agencyGuid && { agencyGuid }),
        items: 500,
        status: 'active',
        userType: 'homeHealth',
      });
      const { data, error } =
        await dataService.getList<IAdminPanelAgencyDoctorsResponse>(
          `${API_ADMIN_PANEL_AGENCY_DOCTORS}?${filtersString}`,
        );

      if (error || !data) return [];

      const items = data.items ?? [];

      return items.map(
        (item): IMemberForPod => ({
          loginGuid: item.loginGuid,
          name: [item.firstName, item.lastName].filter(Boolean).join(' '),
          role: extractPrimaryRole(item.roles ?? ''),
        }),
      );
    },
    staleTime: FIVE_MINUTES,
  });

  return { members, isLoading, isError };
};
